/**
 * Records Module Query Keys
 *
 * Hierarchical query key factory for RecordTypes and Records.
 * Following TanStack Query best practices for key organization.
 *
 * Key Structure:
 * - ['record-types'] - Root key for all RecordType queries
 * - ['record-types', 'list', tenantId, filters] - List queries
 * - ['record-type', id, tenantId] - Detail queries
 * - ['records'] - Root key for all Record queries
 * - ['records', 'list', tenantId, filters] - List queries
 * - ['record', id, tenantId] - Detail queries
 *
 * @see https://tkdodo.eu/blog/effective-react-query-keys
 */

// ============================================================================
// RecordType Keys
// ============================================================================

/**
 * Entity key for single RecordType queries
 * Used by createEntityCache utility for detail cache operations
 */
export const RECORD_TYPE_ENTITY_KEY = 'record-type';

/**
 * List key for RecordType list queries
 */
export const RECORD_TYPE_LIST_KEY = 'record-types';

/**
 * Query key factory for RecordTypes
 *
 * Provides a type-safe, hierarchical structure for query keys.
 * All keys include tenantId for proper multi-tenant cache scoping.
 */
export const recordTypeKeys = {
  /**
   * Root key - invalidates ALL RecordType queries
   * Usage: queryClient.invalidateQueries({ queryKey: recordTypeKeys.all })
   */
  all: [RECORD_TYPE_LIST_KEY] as const,

  /**
   * List queries root - invalidates all list queries for a tenant
   * Usage: queryClient.invalidateQueries({ queryKey: recordTypeKeys.lists(tenantId) })
   */
  lists: (tenantId: string) =>
    [...recordTypeKeys.all, 'list', tenantId] as const,

  /**
   * Specific list query with tenant and filters
   * Usage: queryKey: recordTypeKeys.list(tenantId, { search: 'NCR' })
   */
  list: (tenantId: string, filters: Record<string, unknown> = {}) =>
    [...recordTypeKeys.lists(tenantId), filters] as const,

  /**
   * Query queries root (for POST /query endpoint)
   */
  queries: (tenantId: string) =>
    [...recordTypeKeys.all, 'query', tenantId] as const,

  /**
   * Specific query request
   */
  query: (tenantId: string, queryRequest: Record<string, unknown>) =>
    [...recordTypeKeys.queries(tenantId), queryRequest] as const,

  /**
   * Detail queries root
   */
  details: () => [...recordTypeKeys.all, 'detail'] as const,

  /**
   * Specific detail query for a single RecordType
   * Usage: queryKey: recordTypeKeys.detail(id, tenantId)
   */
  detail: (id: string, tenantId: string) =>
    [RECORD_TYPE_ENTITY_KEY, id, tenantId] as const,

  /**
   * Revisions for a RecordType
   */
  revisions: (recordTypeId: string, tenantId: string) =>
    [RECORD_TYPE_ENTITY_KEY, recordTypeId, 'revisions', tenantId] as const,

  /**
   * Single revision detail
   */
  revision: (recordTypeId: string, revisionId: string, tenantId: string) =>
    [
      RECORD_TYPE_ENTITY_KEY,
      recordTypeId,
      'revision',
      revisionId,
      tenantId,
    ] as const,

  /**
   * Approval status for a RecordType
   */
  approval: (recordTypeId: string, tenantId: string) =>
    [RECORD_TYPE_ENTITY_KEY, recordTypeId, 'approval', tenantId] as const,

  /**
   * Per-revision approval queries (used by Info tab + History tab to avoid
   * cross-revision cache bleed from the shared approval key)
   */
  revisionApproval: (
    recordTypeId: string,
    revisionId: string,
    tenantId: string
  ) =>
    [
      RECORD_TYPE_ENTITY_KEY,
      recordTypeId,
      'revision-approval',
      revisionId,
      tenantId,
    ] as const,

  /**
   * Prefix key for invalidating all per-revision approvals for a RecordType
   */
  revisionApprovals: (recordTypeId: string) =>
    [RECORD_TYPE_ENTITY_KEY, recordTypeId, 'revision-approval'] as const,

  /**
   * Pending approvals for current user
   */
  pendingApprovals: (tenantId: string) =>
    [...recordTypeKeys.all, 'pending-approvals', tenantId] as const,

  /**
   * Next suffix for a RecordType's display ID
   */
  nextSuffix: (recordTypeId: string, tenantId: string) =>
    [RECORD_TYPE_ENTITY_KEY, recordTypeId, 'next-suffix', tenantId] as const,
};

// ============================================================================
// Record Keys
// ============================================================================

/**
 * Entity key for single Record queries
 */
export const RECORD_ENTITY_KEY = 'record';

/**
 * List key for Record list queries
 */
export const RECORD_LIST_KEY = 'records';

/**
 * Query key factory for Records
 */
export const recordKeys = {
  /**
   * Root key - invalidates ALL Record queries
   */
  all: [RECORD_LIST_KEY] as const,

  /**
   * List queries root
   */
  lists: (tenantId: string) => [...recordKeys.all, 'list', tenantId] as const,

  /**
   * Specific list query with filters
   */
  list: (tenantId: string, filters: Record<string, unknown> = {}) =>
    [...recordKeys.lists(tenantId), filters] as const,

  /**
   * Query queries root (for POST /query endpoint)
   */
  queries: (tenantId: string) =>
    [...recordKeys.all, 'query', tenantId] as const,

  /**
   * Specific query request
   */
  query: (tenantId: string, queryRequest: Record<string, unknown>) =>
    [...recordKeys.queries(tenantId), queryRequest] as const,

  /**
   * Detail queries root
   */
  details: () => [...recordKeys.all, 'detail'] as const,

  /**
   * Specific detail query for a single Record
   */
  detail: (id: string, tenantId: string) =>
    [RECORD_ENTITY_KEY, id, tenantId] as const,

  /**
   * My tasks (records with pending steps for current user)
   */
  myTasks: (tenantId: string) =>
    [...recordKeys.all, 'my-tasks', tenantId] as const,

  /**
   * Attachments for a specific Record
   */
  attachments: (recordId: string, tenantId: string) =>
    [RECORD_ENTITY_KEY, recordId, 'attachments', tenantId] as const,
};

/**
 * Example Usage:
 *
 * // In a list query hook
 * useQuery({
 *   queryKey: recordTypeKeys.list(tenantId, { status: 'published' }),
 *   queryFn: () => api.getRecordTypes({ status: 'published' }),
 * });
 *
 * // In a detail query hook
 * useQuery({
 *   queryKey: recordTypeKeys.detail(recordTypeId, tenantId),
 *   queryFn: () => api.getRecordType(recordTypeId),
 * });
 *
 * // In a query (POST) hook
 * useQuery({
 *   queryKey: recordTypeKeys.query(tenantId, queryRequest),
 *   queryFn: () => api.queryRecordTypes(queryRequest),
 * });
 *
 * // Invalidate all RecordType queries
 * queryClient.invalidateQueries({ queryKey: recordTypeKeys.all });
 *
 * // Invalidate all list queries (keeps detail queries)
 * queryClient.invalidateQueries({ queryKey: recordTypeKeys.lists(tenantId) });
 *
 * // My tasks query
 * useQuery({
 *   queryKey: recordKeys.myTasks(tenantId),
 *   queryFn: () => api.getMyTasks(),
 * });
 */
